/**
 * Connection Testing Panel
 * Provides comprehensive connection testing for all platforms and services
 */

import React, { useState } from 'react';
import { useConfigStore } from '../stores/configStore';
import { configurationAPI } from '../services/configurationAPI';

interface TestResult {
  name: string;
  status: 'idle' | 'testing' | 'success' | 'failed';
  message: string;
  details?: any;
  timestamp?: string;
}

export const ConnectionTestingPanel: React.FC = () => {
  const { config } = useConfigStore();
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const addTestResult = (result: TestResult) => {
    setTestResults((prev) => {
      const existing = prev.findIndex((r) => r.name === result.name);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = result;
        return updated;
      }
      return [...prev, result];
    });
  };

  const testDatabaseConnection = async () => {
    addTestResult({
      name: 'Database Connection',
      status: 'testing',
      message: 'Testing database connection...',
    });

    try {
      const result = await configurationAPI.testDatabaseConnection(config.database);
      addTestResult({
        name: 'Database Connection',
        status: result.success ? 'success' : 'failed',
        message: result.message,
        details: result.details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      addTestResult({
        name: 'Database Connection',
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const testEBSConnection = async () => {
    addTestResult({
      name: 'EBS Connection',
      status: 'testing',
      message: 'Testing EBS connection...',
    });

    try {
      const result = await configurationAPI.testEBSConnection(config.ebs);
      addTestResult({
        name: 'EBS Connection',
        status: result.success ? 'success' : 'failed',
        message: result.message,
        details: result.details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      addTestResult({
        name: 'EBS Connection',
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const testAISConnection = async () => {
    addTestResult({
      name: 'AIS Admin Panel Connection',
      status: 'testing',
      message: 'Testing AIS Admin Panel connection...',
    });

    try {
      const result = await configurationAPI.testAISConnection(config.aisAdminPanel);
      addTestResult({
        name: 'AIS Admin Panel Connection',
        status: result.success ? 'success' : 'failed',
        message: result.message,
        details: result.details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      addTestResult({
        name: 'AIS Admin Panel Connection',
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const testAPIConnection = async () => {
    addTestResult({
      name: 'API Configuration',
      status: 'testing',
      message: 'Testing API configuration...',
    });

    try {
      const result = await configurationAPI.testAPIConfiguration(config.api);
      addTestResult({
        name: 'API Configuration',
        status: result.success ? 'success' : 'failed',
        message: result.message,
        details: result.details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      addTestResult({
        name: 'API Configuration',
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const testAISPortalConnection = async () => {
    addTestResult({
      name: 'AIS Portal Connection',
      status: 'testing',
      message: 'Testing AIS Portal connection...',
    });

    try {
      const result = await configurationAPI.testAISConnection(config.aisPortal);
      addTestResult({
        name: 'AIS Portal Connection',
        status: result.success ? 'success' : 'failed',
        message: result.message,
        details: result.details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      addTestResult({
        name: 'AIS Portal Connection',
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const testAdminPanelConnection = async () => {
    addTestResult({
      name: 'Admin Panel Connection',
      status: 'testing',
      message: 'Testing Admin Panel connection...',
    });

    try {
      const result = await configurationAPI.testAISConnection(config.adminPanel);
      addTestResult({
        name: 'Admin Panel Connection',
        status: result.success ? 'success' : 'failed',
        message: result.message,
        details: result.details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      addTestResult({
        name: 'Admin Panel Connection',
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);

    await testDatabaseConnection();
    await testEBSConnection();
    await testAISConnection();
    await testAPIConnection();
    await testAISPortalConnection();
    await testAdminPanelConnection();

    setIsRunning(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return '✓';
      case 'failed':
        return '✗';
      case 'testing':
        return '⟳';
      default:
        return '○';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'testing':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Connection Testing</h3>
        <button
          onClick={runAllTests}
          disabled={isRunning}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          {isRunning ? 'Testing...' : 'Run All Tests'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Database Connection */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-medium">Database Connection</h4>
            <button
              onClick={testDatabaseConnection}
              disabled={isRunning}
              className="text-sm px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:bg-gray-100"
            >
              Test
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            {config.database?.type === 'oracle' ? 'Oracle' : 'PostgreSQL'} Database
          </p>
          {testResults.find((r) => r.name === 'Database Connection') && (
            <div className={`p-3 rounded ${getStatusColor(testResults.find((r) => r.name === 'Database Connection')?.status || '')}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getStatusIcon(testResults.find((r) => r.name === 'Database Connection')?.status || '')}</span>
                <span className="text-sm">{testResults.find((r) => r.name === 'Database Connection')?.message}</span>
              </div>
            </div>
          )}
        </div>

        {/* EBS Connection */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-medium">EBS Connection</h4>
            <button
              onClick={testEBSConnection}
              disabled={isRunning}
              className="text-sm px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:bg-gray-100"
            >
              Test
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">Oracle E-Business Suite</p>
          {testResults.find((r) => r.name === 'EBS Connection') && (
            <div className={`p-3 rounded ${getStatusColor(testResults.find((r) => r.name === 'EBS Connection')?.status || '')}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getStatusIcon(testResults.find((r) => r.name === 'EBS Connection')?.status || '')}</span>
                <span className="text-sm">{testResults.find((r) => r.name === 'EBS Connection')?.message}</span>
              </div>
            </div>
          )}
        </div>

        {/* AIS Admin Panel Connection */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-medium">AIS Admin Panel</h4>
            <button
              onClick={testAISConnection}
              disabled={isRunning}
              className="text-sm px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:bg-gray-100"
            >
              Test
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">AIS Admin Panel Connection</p>
          {testResults.find((r) => r.name === 'AIS Admin Panel Connection') && (
            <div className={`p-3 rounded ${getStatusColor(testResults.find((r) => r.name === 'AIS Admin Panel Connection')?.status || '')}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getStatusIcon(testResults.find((r) => r.name === 'AIS Admin Panel Connection')?.status || '')}</span>
                <span className="text-sm">{testResults.find((r) => r.name === 'AIS Admin Panel Connection')?.message}</span>
              </div>
            </div>
          )}
        </div>

        {/* API Configuration */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-medium">API Configuration</h4>
            <button
              onClick={testAPIConnection}
              disabled={isRunning}
              className="text-sm px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:bg-gray-100"
            >
              Test
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">API Configuration Validation</p>
          {testResults.find((r) => r.name === 'API Configuration') && (
            <div className={`p-3 rounded ${getStatusColor(testResults.find((r) => r.name === 'API Configuration')?.status || '')}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getStatusIcon(testResults.find((r) => r.name === 'API Configuration')?.status || '')}</span>
                <span className="text-sm">{testResults.find((r) => r.name === 'API Configuration')?.message}</span>
              </div>
            </div>
          )}
        </div>

        {/* AIS Portal Connection */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-medium">AIS Portal</h4>
            <button
              onClick={testAISPortalConnection}
              disabled={isRunning}
              className="text-sm px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:bg-gray-100"
            >
              Test
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">AIS Portal Connection</p>
          {testResults.find((r) => r.name === 'AIS Portal Connection') && (
            <div className={`p-3 rounded ${getStatusColor(testResults.find((r) => r.name === 'AIS Portal Connection')?.status || '')}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getStatusIcon(testResults.find((r) => r.name === 'AIS Portal Connection')?.status || '')}</span>
                <span className="text-sm">{testResults.find((r) => r.name === 'AIS Portal Connection')?.message}</span>
              </div>
            </div>
          )}
        </div>

        {/* Admin Panel Connection */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-medium">Admin Panel</h4>
            <button
              onClick={testAdminPanelConnection}
              disabled={isRunning}
              className="text-sm px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:bg-gray-100"
            >
              Test
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">Admin Panel Connection</p>
          {testResults.find((r) => r.name === 'Admin Panel Connection') && (
            <div className={`p-3 rounded ${getStatusColor(testResults.find((r) => r.name === 'Admin Panel Connection')?.status || '')}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getStatusIcon(testResults.find((r) => r.name === 'Admin Panel Connection')?.status || '')}</span>
                <span className="text-sm">{testResults.find((r) => r.name === 'Admin Panel Connection')?.message}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Test Summary */}
      {testResults.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-2">Test Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-green-600 font-semibold">
                {testResults.filter((r) => r.status === 'success').length}
              </span>
              <span className="text-gray-600"> Passed</span>
            </div>
            <div>
              <span className="text-red-600 font-semibold">
                {testResults.filter((r) => r.status === 'failed').length}
              </span>
              <span className="text-gray-600"> Failed</span>
            </div>
            <div>
              <span className="text-gray-600">
                {new Date(testResults[testResults.length - 1]?.timestamp || '').toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionTestingPanel;
